"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatDateTime, cn } from "@/lib/utils";
import { MapPin, Trophy, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { Flag } from "@/components/shared/flags";
import Link from "next/link";
import { useRouter } from "next/navigation";

interface Krajina {
  nazov: string;
  kod: string;
}

interface SkupinaInfo {
  nazov: string;
  slug: string;
}

interface ZapasWithKrajina {
  id: string;
  datum_cas: string;
  miesto: string | null;
  skore_domaci: number | null;
  skore_hosti: number | null;
  stav: string;
  faza: string;
  skupina_id: string | null;
  poznamka: string | null;
  domaci: Krajina | null;
  hosti: Krajina | null;
  skupina: SkupinaInfo | null;
}

interface ZapasSDetailmi {
  id: string;
  datum_cas: string;
  miesto: string | null;
  skore_domaci: number | null;
  skore_hosti: number | null;
  stav: string;
  faza: string;
  skupina_id: string | null;
  skupina_nazov: string | null;
  skupina_slug: string | null;
  poznamka: string | null;
  domaci_nazov: string;
  domaci_kod: string;
  hosti_nazov: string;
  hosti_kod: string;
}

const HEADER_SECTION_CLASSES = cn(
  "bg-muted/40 px-4 py-3 border-b border-muted/20 flex flex-col sm:flex-row",
  "sm:justify-between sm:items-center text-[10px] font-black uppercase",
  "tracking-[0.15em] text-muted-foreground/80 gap-2 sm:gap-4",
);
const SCORE_CONTAINER_CLASSES = cn(
  "flex flex-col items-center gap-3 px-4 sm:px-6 md:px-8",
  "justify-center min-w-[120px] sm:min-w-[140px]",
);
const TEAM_SECTION_CLASSES = cn(
  "flex-1 text-center space-y-2 min-w-0 group/team",
);
const TEAM_LAYOUT_CLASSES = cn("flex items-center justify-center gap-3");
const FLAG_SIZE = 32;

export function ZapasyList() {
  const LOADING_TEXT = "Načítavam zápasy...";
  const NO_MATCHES_TEXT = "Zatiaľ nie sú naplánované žiadne zápasy.";
  const VS_TEXT = "VS";
  const SCORE_DIVIDER = ":";
  const router = useRouter();

  const [zapasy, setZapasy] = useState<ZapasSDetailmi[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function nacitajZapasy() {
      const supabase = createClient();

      const [zapasyRes, skupinyRes] = await Promise.all([
        supabase
          .from("zapas")
          .select(
            `
            *,
            domaci:krajina!zapas_domacikrajinaid_fkey(nazov, kod),
            hosti:krajina!zapas_hostikrajinaid_fkey(nazov, kod)
          `,
          )
          .order("datum_cas", { ascending: true }),
        supabase.from("skupina").select("id, nazov, slug"),
      ]);

      if (zapasyRes.error) {
        setLoading(false);
        return;
      }

      const skupinyMap = new Map<string, { nazov: string; slug: string }>();
      if (skupinyRes.data) {
        for (const s of skupinyRes.data) {
          skupinyMap.set(s.id, { nazov: s.nazov, slug: s.slug });
        }
      }

      const zapasyData = zapasyRes.data as unknown as ZapasWithKrajina[];
      const transformovane: ZapasSDetailmi[] = zapasyData.map((z) => {
        const sk = z.skupina_id ? skupinyMap.get(z.skupina_id) : null;
        return {
          id: z.id,
          datum_cas: z.datum_cas,
          miesto: z.miesto,
          skore_domaci: z.skore_domaci,
          skore_hosti: z.skore_hosti,
          stav: z.stav,
          faza: z.faza,
          skupina_id: z.skupina_id,
          skupina_nazov: sk?.nazov || null,
          skupina_slug: sk?.slug || null,
          poznamka: z.poznamka,
          domaci_nazov: z.domaci?.nazov || "TO BE DETERMINED",
          domaci_kod: z.domaci?.kod || "TBD",
          hosti_nazov: z.hosti?.nazov || "TO BE DETERMINED",
          hosti_kod: z.hosti?.kod || "TBD",
        };
      });
      setZapasy(transformovane);
      setLoading(false);
    }

    nacitajZapasy();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="flex flex-col items-center gap-6">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent shadow-lg shadow-primary/10"></div>
          <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest animate-pulse">
            {LOADING_TEXT}
          </p>
        </div>
      </div>
    );
  }

  if (zapasy.length === 0) {
    return (
      <Card className="border-dashed border-muted/30 bg-muted/5 shadow-none">
        <CardContent className="py-20 text-center flex flex-col items-center gap-4">
          <div className="p-4 bg-muted/50 rounded-full">
            <Trophy className="h-10 w-10 text-muted-foreground/30" />
          </div>
          <p className="text-muted-foreground font-medium">{NO_MATCHES_TEXT}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6">
      {zapasy.map((zapas, idx) => (
        <motion.div
          key={zapas.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: idx * 0.1 }}
          className="group"
        >
          <Link href={`/zapas/${zapas.id}`} className="block">
            <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-muted/20 bg-background/50 backdrop-blur-sm cursor-pointer">
              <div className={HEADER_SECTION_CLASSES}>
                <div className="flex items-center gap-2 text-primary font-black">
                  <Trophy className="h-3.5 w-3.5" />
                  {zapas.faza}
                  {zapas.skupina_nazov && (
                    <span
                      className="text-muted-foreground/60 hover:text-primary transition-colors cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(`/skupiny/${zapas.skupina_slug}`);
                      }}
                    >
                      • {zapas.skupina_nazov}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 font-bold">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDateTime(zapas.datum_cas)}
                </div>
              </div>
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center justify-between gap-4">
                  <div className={TEAM_SECTION_CLASSES}>
                    <div
                      className="block cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(
                          `/krajina/${zapas.domaci_kod.toLowerCase()}`,
                        );
                      }}
                    >
                      <div className={TEAM_LAYOUT_CLASSES}>
                        <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                          {zapas.domaci_kod}
                        </div>
                        <Flag kod={zapas.domaci_kod} size={FLAG_SIZE} />
                      </div>
                      <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto hover:text-primary transition-colors">
                        {zapas.domaci_nazov}
                      </div>
                    </div>
                  </div>

                  <div className={SCORE_CONTAINER_CLASSES}>
                    {zapas.stav === "ukonceny" ? (
                      <div className="text-4xl md:text-5xl font-black tracking-tighter tabular-nums drop-shadow-sm">
                        {zapas.skore_domaci} {SCORE_DIVIDER} {zapas.skore_hosti}
                      </div>
                    ) : (
                      <div className="text-2xl font-black text-muted-foreground/30 italic tracking-widest">
                        {VS_TEXT}
                      </div>
                    )}
                    <Badge
                      variant={
                        zapas.stav === "ukonceny" ? "secondary" : "outline"
                      }
                      className={cn(
                        "rounded-full px-4 py-1 text-[10px] font-black uppercase tracking-widest",
                        zapas.stav === "ukonceny"
                          ? "bg-status-success/10 text-status-success border-status-success/20"
                          : "bg-muted/30 text-muted-foreground border-muted/20",
                      )}
                    >
                      {zapas.stav}
                    </Badge>
                  </div>

                  <div className={TEAM_SECTION_CLASSES}>
                    <div
                      className="block cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(
                          `/krajina/${zapas.hosti_kod.toLowerCase()}`,
                        );
                      }}
                    >
                      <div className={TEAM_LAYOUT_CLASSES}>
                        <Flag kod={zapas.hosti_kod} size={FLAG_SIZE} />
                        <div className="text-3xl md:text-4xl font-black tracking-tighter group-hover/team:text-primary transition-colors uppercase">
                          {zapas.hosti_kod}
                        </div>
                      </div>
                      <div className="text-[10px] md:text-xs font-black text-muted-foreground/60 uppercase tracking-[0.2em] truncate max-w-[120px] mx-auto hover:text-primary transition-colors">
                        {zapas.hosti_nazov}
                      </div>
                    </div>
                  </div>
                </div>

                {zapas.miesto && (
                  <div className="mt-8 pt-6 border-t border-muted/10 flex justify-center items-center gap-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                    <MapPin className="h-3.5 w-3.5 text-primary/60" />
                    <span className="truncate">{zapas.miesto}</span>
                  </div>
                )}
                {zapas.poznamka && (
                  <div className="mt-4 flex justify-center items-center gap-2 text-[10px] font-medium text-muted-foreground/80 italic">
                    <span className="truncate">{zapas.poznamka}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        </motion.div>
      ))}
    </div>
  );
}
